import React, { useCallback } from "react";
import type { Supplier } from "@meupedaco/shared";
import { CrudPage } from "../../components/CrudPage";
import { useAuth } from "../../auth/AuthContext";

export const SuppliersPage: React.FC = () => {
  const { client } = useAuth();

  const fetchEntities = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/entities?q=${encodeURIComponent(query)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as { data: Array<{ id: number; name: string; document?: string | null }> };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.name}${item.document ? ` (${item.document})` : ""}`,
      }));
    },
    [client]
  );

  const fetchAccounts = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/accounts?q=${encodeURIComponent(query)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; code: string; name: string }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.code} - ${item.name}`,
      }));
    },
    [client]
  );

  return (
    <CrudPage<Supplier>
      title="Fornecedores"
      endpoint="/modules/suppliers"
      searchPlaceholder="Buscar por nome, documento..."
      columns={[
        { label: "Fornecedor", render: (item) => item.entity.name },
        { label: "Documento", render: (item) => item.entity.document ?? "-" },
        {
          label: "Conta padrao",
          render: (item) =>
            item.defaultAccount
              ? `${item.defaultAccount.code} - ${item.defaultAccount.name}`
              : "-",
        },
        { label: "Ativo", render: (item) => (item.isActive ? "Sim" : "Nao") },
      ]}
      formFields={[
        {
          name: "entityRefId",
          label: "Entidade",
          type: "autocomplete",
          placeholder: "Digite para buscar entidade",
          fetchOptions: fetchEntities,
          getIdFromItem: (item) => item.entityId,
          getLabelFromItem: (item) => item.entity.name,
        },
        {
          name: "defaultAccountId",
          label: "Conta contabil padrao",
          type: "autocomplete",
          placeholder: "Buscar conta",
          fetchOptions: fetchAccounts,
          getIdFromItem: (item) => item.defaultAccountId ?? null,
          getLabelFromItem: (item) =>
            item.defaultAccount
              ? `${item.defaultAccount.code} - ${item.defaultAccount.name}`
              : "",
        },
        { name: "isActive", label: "Ativo", type: "checkbox" },
      ]}
      initialValues={{
        entityRefId: null,
        defaultAccountId: null,
        isActive: true,
      }}
      mapToPayload={(values) => ({
        entityId: Number(values.entityRefId),
        defaultAccountId: values.defaultAccountId
          ? Number(values.defaultAccountId)
          : null,
        isActive: values.isActive,
      })}
      permissionWrite="SUPPLIER_WRITE"
    />
  );
};
